// src/services/session-logs-db.service.js

const DB_NAME = 'forum_session_logs';
const DB_VERSION = 1;

const SESSIONS_STORE = 'sessions';
const ENTRIES_STORE = 'entries';
const ENTRIES_BY_ROOM_INDEX = 'by_room_time';

const LEGACY_STORAGE_KEY = 'forum_session_logs_v1';
const PENDING_STORAGE_KEY = 'forum_session_logs_pending';

let dbPromise = null;

function requestResult(request) {
    return new Promise((resolve, reject) => {
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}

function roomEntriesRange(roomCode) {
    // В IndexedDB массив сортируется после любой строки, поэтому диапазон покрывает все timestamp комнаты.
    return IDBKeyRange.bound([roomCode], [roomCode, []]);
}

function runTransaction(db, mode, work) {
    return new Promise((resolve, reject) => {
        const tx = db.transaction([SESSIONS_STORE, ENTRIES_STORE], mode);
        let result;

        tx.oncomplete = () => resolve(result);
        tx.onabort = () => reject(tx.error);

        work({
            sessions: tx.objectStore(SESSIONS_STORE),
            entries: tx.objectStore(ENTRIES_STORE),
        })
            .then(value => {
                result = value;
            })
            .catch(error => {
                reject(error);

                try {
                    tx.abort();
                } catch {
                    // Транзакция уже завершилась, откатывать нечего.
                }
            });
    });
}

function putEntries(stores, roomCode, entries) {
    return Promise.all(entries.map(entry => requestResult(stores.entries.put({ ...entry, roomCode }))));
}

async function deleteRoomEntries(stores, roomCode) {
    const keys = await requestResult(stores.entries.index(ENTRIES_BY_ROOM_INDEX).getAllKeys(roomEntriesRange(roomCode)));

    await Promise.all(keys.map(key => requestResult(stores.entries.delete(key))));
}

async function readRoomEntries(stores, roomCode) {
    const entries = await requestResult(stores.entries.index(ENTRIES_BY_ROOM_INDEX).getAll(roomEntriesRange(roomCode)));

    entries.forEach(entry => {
        delete entry.roomCode;
    });

    return entries;
}

function isStorableSession(session) {
    return Boolean(session && session.roomCode && Array.isArray(session.entries));
}

function isStorableEntry(entry) {
    return Boolean(entry && entry.id && entry.timestamp);
}

async function appendToStores(stores, { roomCode, userId, entries, maxSessions, maxEntries }) {
    const session = await requestResult(stores.sessions.get(roomCode));
    const lastTimestamp = entries[entries.length - 1].timestamp;

    await requestResult(stores.sessions.put({
        roomCode,
        userId,
        startedAt: entries[0].timestamp,
        ...session,
        // Отложенный пакет может прийти позже более новых записей другой вкладки.
        updatedAt: session?.updatedAt > lastTimestamp ? session.updatedAt : lastTimestamp,
    }));

    await putEntries(stores, roomCode, entries);

    const index = stores.entries.index(ENTRIES_BY_ROOM_INDEX);
    const range = roomEntriesRange(roomCode);
    const excess = await requestResult(index.count(range)) - maxEntries;

    if (excess > 0) {
        const oldestKeys = await requestResult(index.getAllKeys(range, excess));

        await Promise.all(oldestKeys.map(key => requestResult(stores.entries.delete(key))));
    }

    const allSessions = await requestResult(stores.sessions.getAll());
    const evicted = allSessions
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
        .slice(maxSessions);

    await Promise.all(evicted.map(async item => {
        await requestResult(stores.sessions.delete(item.roomCode));
        await deleteRoomEntries(stores, item.roomCode);
    }));
}

/**
 * Транзакция IndexedDB не успевает завершиться при выгрузке страницы, поэтому остаток буфера
 * на pagehide кладём сюда синхронно, а в базу он попадает при следующем обращении к ней.
 * Записи пишутся по id, так что повторная запись уже сохранённого пакета дублей не создаст.
 */
export function stashPendingEntries(batch) {
    try {
        const pending = JSON.parse(localStorage.getItem(PENDING_STORAGE_KEY)) || [];

        localStorage.setItem(PENDING_STORAGE_KEY, JSON.stringify([...pending, batch]));
    } catch (error) {
        console.warn('[SessionLogsDb] Failed to stash pending entries', error);
    }
}

async function drainPendingEntries(db) {
    const raw = localStorage.getItem(PENDING_STORAGE_KEY);

    if (raw === null) return;

    let batches = [];

    try {
        batches = JSON.parse(raw);
    } catch (error) {
        console.warn('[SessionLogsDb] Dropping unreadable pending entries', error);
    }

    await runTransaction(db, 'readwrite', async stores => {
        for (const batch of batches) {
            await appendToStores(stores, batch);
        }
    });

    // Пока шла транзакция, другая вкладка могла добавить свой пакет: тогда заберём его в следующий раз.
    if (localStorage.getItem(PENDING_STORAGE_KEY) === raw) {
        localStorage.removeItem(PENDING_STORAGE_KEY);
    }
}

/**
 * Переносит логи, накопленные в localStorage до перехода на IndexedDB. Ключ удаляем только после
 * успешной транзакции: при сбое перенос повторится при следующем открытии базы.
 */
async function migrateLegacyLogs(db) {
    const raw = localStorage.getItem(LEGACY_STORAGE_KEY);

    if (raw === null) return;

    let legacySessions = [];

    try {
        legacySessions = JSON.parse(raw);
    } catch (error) {
        console.warn('[SessionLogsDb] Dropping unreadable legacy logs', error);
    }

    if (Array.isArray(legacySessions) && legacySessions.length) {
        await runTransaction(db, 'readwrite', async stores => {
            await Promise.all(legacySessions.filter(isStorableSession).map(async ({ entries, ...session }) => {
                // Сессию могла уже создать другая вкладка: её данные новее, поэтому не перезаписываем.
                const existing = await requestResult(stores.sessions.get(session.roomCode));

                if (!existing) {
                    await requestResult(stores.sessions.put(session));
                }

                await putEntries(stores, session.roomCode, entries.filter(isStorableEntry));
            }));
        });
    }

    localStorage.removeItem(LEGACY_STORAGE_KEY);
}

function openDb() {
    if (dbPromise) return dbPromise;

    dbPromise = new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onupgradeneeded = () => {
            const db = request.result;

            db.createObjectStore(SESSIONS_STORE, { keyPath: 'roomCode' });

            db.createObjectStore(ENTRIES_STORE, { keyPath: 'id' })
                .createIndex(ENTRIES_BY_ROOM_INDEX, ['roomCode', 'timestamp']);
        };

        request.onsuccess = () => {
            const db = request.result;

            db.onversionchange = () => {
                db.close();
                dbPromise = null;
            };

            db.onclose = () => {
                dbPromise = null;
            };

            resolve(db);
        };

        request.onerror = () => reject(request.error);
    })
        .then(async db => {
            try {
                await migrateLegacyLogs(db);
            } catch (error) {
                console.warn('[SessionLogsDb] Legacy logs migration failed', error);
            }

            return db;
        })
        .catch(error => {
            dbPromise = null;
            throw error;
        });

    return dbPromise;
}

async function withStores(mode, work) {
    const db = await openDb();

    try {
        await drainPendingEntries(db);
    } catch (error) {
        console.warn('[SessionLogsDb] Failed to drain pending entries', error);
    }

    return runTransaction(db, mode, work);
}

export function appendSessionEntries(batch) {
    return withStores('readwrite', stores => appendToStores(stores, batch));
}

export function readAllSessions() {
    return withStores('readonly', async stores => {
        const sessions = await requestResult(stores.sessions.getAll());

        return Promise.all(sessions.map(async session => ({
            ...session,
            entries: await readRoomEntries(stores, session.roomCode),
        })));
    });
}

export function readSessionSummaries() {
    return withStores('readonly', async stores => {
        const sessions = await requestResult(stores.sessions.getAll());
        const index = stores.entries.index(ENTRIES_BY_ROOM_INDEX);

        return Promise.all(sessions.map(async session => ({
            ...session,
            entryCount: await requestResult(index.count(roomEntriesRange(session.roomCode))),
        })));
    });
}

export function readSession(roomCode) {
    return withStores('readonly', async stores => {
        const session = await requestResult(stores.sessions.get(roomCode));

        if (!session) return null;

        return {
            ...session,
            entries: await readRoomEntries(stores, roomCode),
        };
    });
}

export function importSessions(importedSessions) {
    return withStores('readwrite', async stores => {
        await Promise.all(importedSessions.filter(isStorableSession).map(async ({ entries, ...imported }) => {
            const existing = await requestResult(stores.sessions.get(imported.roomCode));

            await requestResult(stores.sessions.put({
                ...existing,
                ...imported,
                startedAt: existing?.startedAt || imported.startedAt,
                updatedAt: new Date().toISOString(),
            }));

            await putEntries(stores, imported.roomCode, entries.filter(isStorableEntry));
        }));
    });
}

export function clearSessionEntries(roomCode) {
    return withStores('readwrite', async stores => {
        const session = await requestResult(stores.sessions.get(roomCode));

        if (!session) return;

        const now = new Date().toISOString();

        await requestResult(stores.sessions.put({ ...session, updatedAt: now, clearedAt: now }));
        await deleteRoomEntries(stores, roomCode);
    });
}

export function deleteSession(roomCode) {
    return withStores('readwrite', async stores => {
        await requestResult(stores.sessions.delete(roomCode));
        await deleteRoomEntries(stores, roomCode);
    });
}

export function deleteAllSessions() {
    return withStores('readwrite', async stores => {
        await Promise.all([
            requestResult(stores.sessions.clear()),
            requestResult(stores.entries.clear()),
        ]);
    });
}
